/**
 * Profitability Chart Component
 * Shows cumulative revenue, costs, and gross profit over time
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts'
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import type { MonthlyData } from '@/types/ui/dashboard'
import { useFormatters } from '@/hooks/use-formatters'
import { ChartExportMenu } from './ChartExportMenu'
import { TimePeriodToggle } from './TimePeriodToggle'
import { exportToCSV, exportToExcel, exportToPNG, generateFilename } from '@/lib/utils/export-utils'
import { aggregateDataByTimePeriod, formatPeriodForExport, type TimePeriod } from '@/lib/utils/dashboard-utils'
import { useState, useMemo } from 'react'

interface ProfitabilityChartProps {
  data: MonthlyData[]
  contractAmount: number
  projectedCost: number
  engagementId?: string | number
}

export function ProfitabilityChart({ data, contractAmount, projectedCost, engagementId = 0 }: ProfitabilityChartProps) {
  const { formatMoney, formatCurrencyShort } = useFormatters()
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('monthly')

  // Aggregate data based on selected time period
  const aggregatedData = useMemo(() => {
    return aggregateDataByTimePeriod(data, timePeriod)
  }, [data, timePeriod])

  // Calculate cumulative profitability data with running totals
  let cumulativeRevenue = 0
  let cumulativeCosts = 0
  const chartData = aggregatedData.map((item) => {
    cumulativeRevenue += item.revenue
    cumulativeCosts += item.costs
    
    return {
      period: item.period,
      // Carried for exports only (MM-DD-YYYY Period column); the axis uses `period`.
      periodDate: item.periodDate,
      revenue: cumulativeRevenue,
      costs: cumulativeCosts,
    }
  })

  // Chart configuration for theming
  const chartConfig = {
    revenue: {
      label: "Revenue",
    },
    costs: {
      label: "Cost",
    },
  } satisfies ChartConfig

  const handleExportCSV = () => {
    const exportData = chartData.map(item => ({
      Period: formatPeriodForExport(item.periodDate),
      Revenue: item.revenue,
      Costs: item.costs,
    }))
    exportToCSV(exportData, generateFilename('Profitability', engagementId, 'csv'))
  }

  const handleExportExcel = () => {
    const exportData = chartData.map(item => ({
      Period: formatPeriodForExport(item.periodDate),
      Revenue: item.revenue,
      Costs: item.costs,
    }))
    exportToExcel(exportData, generateFilename('Profitability', engagementId, 'xlsx'), 'Profitability')
  }

  const handleExportPNG = () => {
    exportToPNG('profitability-chart', generateFilename('Profitability', engagementId, 'png'))
  }

  const hasData = aggregatedData && aggregatedData.length > 0

  return (
    <Card id="profitability-chart">
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">Profitability</CardTitle>
            <CardDescription>Cumulative revenue, cost, and gross profit trends</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <TimePeriodToggle value={timePeriod} onChange={setTimePeriod} />
            <ChartExportMenu 
              onExportCSV={handleExportCSV}
              onExportExcel={handleExportExcel}
              onExportPNG={handleExportPNG}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex items-center justify-center h-[300px] w-full">
            <p className="text-sm text-muted-foreground">No data available</p>
          </div>
        ) : (
        <ChartContainer config={chartConfig} className="h-[300px] w-full min-w-0 min-h-0">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="period" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrencyShort}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  valueFormatter={(value) => formatMoney(Number(value))}
                />
              }
            />
            <ChartLegend />
            <Line 
              type="monotone" 
              dataKey="revenue" 
              stroke="#3b82f6" 
              strokeWidth={2}
              name="Revenue"
            />
            <Line 
              type="monotone" 
              dataKey="costs" 
              stroke="#ef4444" 
              strokeWidth={2}
              name="Cost"
            />
          </LineChart>
        </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}

